import type { RouteSeo } from './seoData';
import { getSiteUrl } from './seoData';
import { completeGuide } from '@/mocks/richArticles/completeGuide';
import { pillarArticles } from '@/mocks/pillarArticles';

const SITE_NAME = 'AllChinaBuySpread';
const LOGO_URL =
  'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/49750b7389edabeda6497a215e0702e5.png';
const JSON_LD_ID = 'readdy-seo-jsonld';

interface Faq {
  q: string;
  a: string;
}

function organization(siteUrl: string) {
  return {
    '@type': 'Organization',
    '@id': `${siteUrl}/#organization`,
    name: SITE_NAME,
    url: `${siteUrl}/`,
    logo: { '@type': 'ImageObject', url: LOGO_URL },
  };
}

function buildHomeGraph(siteUrl: string) {
  return [
    {
      '@type': 'WebSite',
      '@id': `${siteUrl}/#website`,
      url: `${siteUrl}/`,
      name: SITE_NAME,
      inLanguage: ['en', 'fr', 'da', 'de'],
      publisher: { '@id': `${siteUrl}/#organization` },
    },
    organization(siteUrl),
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/#webpage`,
      url: `${siteUrl}/`,
      name: 'AllChinaBuy Spreadsheet 2026 — Sellers, Finds, QC & Shipping Guides',
      isPartOf: { '@id': `${siteUrl}/#website` },
      about: { '@id': `${siteUrl}/#organization` },
    },
  ];
}

function getFaqs(path: string): Faq[] {
  if (path === '/allchinabuy-spreadsheet-complete-guide') {
    return completeGuide.content.en.faqs as Faq[];
  }
  const article = pillarArticles.find((a) => `/${a.slug}` === path);
  return article ? (article.faqs as Faq[]) : [];
}

function buildArticleGraph(seo: RouteSeo, siteUrl: string) {
  const url = `${siteUrl}${seo.path}`;
  const graph: Record<string, unknown>[] = [organization(siteUrl)];

  const article: Record<string, unknown> = {
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: seo.title,
    description: seo.description,
    datePublished: seo.datePublished,
    dateModified: seo.dateModified || seo.datePublished,
    author: { '@id': `${siteUrl}/#organization` },
    publisher: { '@id': `${siteUrl}/#organization` },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: 'en',
  };

  if (seo.image) {
    article.image = [seo.image];
  }

  graph.push(article);

  const faqs = getFaqs(seo.path);
  if (faqs.length > 0) {
    graph.push({
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  return graph;
}

function injectJsonLd(data: object | null): void {
  const existing = document.getElementById(JSON_LD_ID);
  if (!data) {
    if (existing) existing.remove();
    return;
  }
  let el = existing as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = JSON_LD_ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function applyJsonLd(pathname: string, seo?: RouteSeo): void {
  const siteUrl = getSiteUrl();
  let graph: Record<string, unknown>[] | null = null;
  if (pathname === '/') {
    graph = buildHomeGraph(siteUrl);
  } else if (seo) {
    graph = buildArticleGraph(seo, siteUrl);
  }
  injectJsonLd(graph ? { '@context': 'https://schema.org', '@graph': graph } : null);
}